import React, { useEffect, useContext, useState, useMemo } from "react";
import cx from "classnames";
import { TextField, Tooltip } from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import { Types as EditionTypes } from "../../context/editions.reducer";
import { Context } from "../../context/GroupsManagerContext";
import nonEditableReasonText from "../../utils/nonEditableReasonText";
import splitGroupErrors from "../../utils/splitGroupErrors";
import messageByGroupEditionError from "../../utils/messageByGroupEditionError";
import { AdaptedGroup, HardGroupEditionErrors, SoftGroupEditionErrors } from "../../models";
import css from "./capacityInput.module.scss";

interface Props {
  group: AdaptedGroup;
  isSubgroup: boolean;
  disabled?: boolean;
  inputClassname?: string;
  disableErrorIcons?: boolean;
}

const CapacityInput: React.FC<Props> = props => {
  const { group, isSubgroup, disabled, disableErrorIcons = false } = props;
  const { state, dispatch } = useContext(Context);
  const [inputValue, setInputValue] = useState<string | number>(group?.capacity);
  const [editing, setEditing] = useState(false);

  const onChange = (e: any) => setInputValue(e?.target?.value);

  const [hardErrorsByGroupId, softErrorsByGroupId] = useMemo(() => {
    const [hardErrors, softErrors] = splitGroupErrors(state?.editions?.errorsByGroupId ?? {});
    return [Object.keys(hardErrors[group?.id] ?? {}), Object.keys(softErrors[group?.id] ?? {})];
  }, [state?.editions?.errorsByGroupId[group?.id]]);

  const onBlur = () => {
    setEditing(false);
    const value = inputValue?.toString();
    const shouldReset = value === "" || parseInt(value) < 0;

    if (shouldReset) setInputValue(group?.capacity);

    dispatch({
      type: EditionTypes.UpdateGroupCapacity,
      payload: {
        groupId: group?.id,
        value: shouldReset ? group?.capacity?.toString() : value,
        originalValue: group?.capacity,
      },
    });

    dispatch({ type: EditionTypes.SetGroupsToEdit, payload: {} });
  };

  const capacity = () => {
    // 1. If the user is currently editing the capacity, return its current edition.
    if (editing) {
      return inputValue;
    }

    // 2. If a change capacity was requested, return the one previously setted by the user.
    if (group?.id in (state?.editions?.pendingCapacitiesByGroupId ?? {})) {
      return state?.editions?.pendingCapacitiesByGroupId[group?.id];
    }

    // 3. If an edition was made, return the edited capacity instead of the original one.
    if (
      state?.editions?.byGroupId[group?.id]?.capacity === 0 ||
      Boolean(state?.editions?.byGroupId[group?.id]?.capacity)
    ) {
      return state?.editions?.byGroupId[group?.id]?.capacity;
    }

    // 4. otherwise, return the original group capacity
    return group?.capacity;
  };

  useEffect(() => {
    setInputValue(group?.capacity);
  }, [group]);

  useEffect(() => {
    dispatch({
      type: EditionTypes.UpdateErrorsByGroupId,
      payload: { group, editions: state?.editions?.byGroupId[group?.id], isSubgroup },
    });
  }, [state?.editions?.byGroupId[group?.id]?.capacity]);

  const rightInfo = () => {
    if (hardErrorsByGroupId?.length && !disableErrorIcons) {
      const fstError = hardErrorsByGroupId?.[0];
      return (
        <Tooltip
          label={
            messageByGroupEditionError((fstError ?? "") as keyof HardGroupEditionErrors).abbreviated
          }
        >
          <Icon className={css.tooltipIcon} name="circle-cross" color="error-50" />
        </Tooltip>
      );
    } else if (softErrorsByGroupId?.length && !disableErrorIcons) {
      const fstError = softErrorsByGroupId?.[0];
      return (
        <Tooltip
          label={
            messageByGroupEditionError((fstError ?? "") as keyof SoftGroupEditionErrors).abbreviated
          }
        >
          <Icon className={css.tooltipIcon} name="alert-triangle" color="warning-50" />
        </Tooltip>
      );
    } else if (group?.id in (state?.editions?.byGroupId ?? {})) {
      return (
        <Tooltip label="Cambio pendiente de guardar">
          <Icon className={css.tooltipIcon} name="repeat" color="warning-50" />
        </Tooltip>
      );
    } else if (disabled) {
      return (
        <Tooltip label={nonEditableReasonText(group?.isEditable)}>
          <Icon className={css.tooltipIcon} name="circle-info" color="neutral-70" />
        </Tooltip>
      );
    }

    return <></>;
  };

  const errorsClassNames = () => {
    if (disableErrorIcons || (!hardErrorsByGroupId?.length && !softErrorsByGroupId?.length)) {
      return "";
    }

    return hardErrorsByGroupId?.length ? css["input--error"] : css["input--alert"];
  };

  return (
    <div className={css.container}>
      <TextField
        className={cx(css.input, errorsClassNames())}
        value={capacity()}
        disabled={disabled}
        onChange={onChange}
        type={"number"}
        onBlur={onBlur}
        onFocus={() => {
          setEditing(true);
          setInputValue("");
        }}
      />
      {rightInfo()}
    </div>
  );
};

export default CapacityInput;
